import { IPagination } from "../../interfaces/benefit";
import { ChevronLeftIcon, ChevronRightIcon } from "../ui/Icons";

interface IPaginationProps {
  pagination: IPagination | null;
  limit: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const PAGE_SIZES: number[] = [10, 25, 50, 100];

export default function Pagination({
  pagination,
  limit,
  isLoading,
  onPageChange,
  onLimitChange,
}: IPaginationProps) {
  const currentPage: number = pagination?.currentPage ?? 1;
  const totalPages: number = pagination?.totalPages ?? 0;
  const totalRecords: number = pagination?.totalRecords ?? 0;

  return (
    <div className="pager">
      <span className="pager__info">
        {totalRecords === 0
          ? "Sin registros"
          : `${pagination?.startIndex}–${pagination?.endIndex} de ${totalRecords.toLocaleString("es-CL")}`}
      </span>

      <div className="pager__spacer" />

      <label className="pager__size">
        Filas
        <select
          value={limit}
          disabled={isLoading}
          onChange={(event) => onLimitChange(Number(event.target.value))}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <div className="pager__pages">
        <button
          type="button"
          className="pager__page"
          disabled={!pagination?.hasPrevPage || isLoading}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Página anterior"
        >
          <ChevronLeftIcon size={15} />
        </button>

        {buildPageWindow({ currentPage, totalPages }).map((entry, index) =>
          entry === "gap" ? (
            <span className="pager__gap" key={`gap-${index}`}>
              &hellip;
            </span>
          ) : (
            <button
              type="button"
              key={entry}
              className={
                entry === currentPage ? "pager__page pager__page--active" : "pager__page"
              }
              disabled={entry === currentPage || isLoading}
              onClick={() => onPageChange(entry)}
              aria-current={entry === currentPage ? "page" : undefined}
            >
              {entry}
            </button>
          )
        )}

        <button
          type="button"
          className="pager__page"
          disabled={!pagination?.hasNextPage || isLoading}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Página siguiente"
        >
          <ChevronRightIcon size={15} />
        </button>
      </div>
    </div>
  );
}

/**
 * Ventana de paginas visible: primera, ultima, la actual y sus vecinas, con
 * elipsis en los cortes. Evita renderizar cientos de botones.
 */
function buildPageWindow(params: {
  currentPage: number;
  totalPages: number;
}): Array<number | "gap"> {
  const { currentPage, totalPages } = params;
  if (totalPages <= 0) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | "gap"> = [1];
  const start: number = Math.max(2, currentPage - 1);
  const end: number = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push("gap");

  for (let page = start; page <= end; page++) {
    pages.push(page);
  }

  if (end < totalPages - 1) pages.push("gap");

  pages.push(totalPages);
  return pages;
}
